import React, { useState } from 'react';
import {
  Box,
  Typography,
  Divider,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button
} from '@mui/material';
import AddressDialog from './AddressDialog';

const ReviewSection = ({
  formData,
  panDetails,
  gstDetails,
  tanDetails,
  chequeDetails,
  bankDetails,
  aadhaarDetails,
  altDocType,
  incFileLink,
  partnershipFileLink,
  passbookFileLink,
  parseAddress,
  additionalAddress,
  checked,
  onCheckboxChange,
  open,
  onClose,
  onInputChange,
  onPanNameChange,
  onPanAddressChange,
}) => {
  const { statusOfCustomer, gstStatus } = formData;
  const isNormalRegistered = gstStatus === 'Normal Registered';
  const isForeignEntity = statusOfCustomer === 'Foreign Entity';

  // PAN name override state
  const [nameChecked, setNameChecked] = useState(false);
  const [nameDialogOpen, setNameDialogOpen] = useState(false);
  const [nameInputValue, setNameInputValue] = useState('');

  // Address override state
  const emptyAddress = { address: '', city: '', district: '', state: '', pincode: '' };
  const [addressChecked, setAddressChecked] = useState(false);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [addressInputValue, setAddressInputValue] = useState(emptyAddress);

  const handleNameCheckboxChange = (e) => {
    if (e.target.checked) {
      setNameInputValue(panDetails?.ChangeName ?? panDetails?.name ?? '');
      setNameDialogOpen(true);
      setNameChecked(true);
    } else {
      setNameChecked(false);
      onPanNameChange?.(null);
    }
  };

  const handleCheckedClick = () => {
    if (nameChecked && !nameDialogOpen) {
      setNameInputValue(panDetails?.ChangeName ?? panDetails?.name ?? '');
      setNameDialogOpen(true);
    }
  };

  const handleNameDialogConfirm = () => {
    if (nameInputValue.trim()) {
      onPanNameChange?.(nameInputValue.trim());
      setNameChecked(true);
    }
    setNameDialogOpen(false);
  };

  const handleNameDialogClose = () => {
    if (!panDetails?.ChangeName) {
      setNameChecked(false);
    }
    setNameDialogOpen(false);
  };

  const handleAddressCheckboxChange = (e) => {
    if (e.target.checked) {
      const existing = panDetails?.ChangeAddress ?? emptyAddress;
      setAddressInputValue({ ...emptyAddress, ...existing });
      setAddressDialogOpen(true);
      setAddressChecked(true);
    } else {
      setAddressChecked(false);
      onPanAddressChange?.(null);
    }
  };

  const handleAddressCheckedClick = () => {
    if (addressChecked && !addressDialogOpen) {
      const existing = panDetails?.ChangeAddress ?? emptyAddress;
      setAddressInputValue({ ...emptyAddress, ...existing });
      setAddressDialogOpen(true);
    }
  };

  const handleAddressDialogConfirm = () => {
    if (addressInputValue.address.trim()) {
      onPanAddressChange?.({ ...addressInputValue });
      setAddressChecked(true);
    }
    setAddressDialogOpen(false);
  };

  const handleAddressDialogClose = () => {
    if (!panDetails?.ChangeAddress) {
      setAddressChecked(false);
    }
    setAddressDialogOpen(false);
  };

  const renderPanDetails = () => (
    <>
      <Typography variant="h6" color="secondary">PAN Details</Typography>
      <Divider />
      <Typography>
        <strong>Name:</strong> {panDetails?.ChangeName ?? panDetails?.name ?? '-'}
      </Typography>
      <Typography><strong>PAN Number:</strong> {panDetails?.panNumber || '-'}</Typography>
      <Typography><strong>Date:</strong> {panDetails?.dob || panDetails?.dateOfIncorporation || '-'}</Typography>

      <FormControlLabel
        control={
          <Checkbox
            checked={nameChecked}
            onChange={handleNameCheckboxChange}
            onClick={handleCheckedClick}
            color="primary"
          />
        }
        label="Change Name / Add Name"
        sx={{
          mt: 0.5,
          '& .MuiFormControlLabel-label': {
            color: 'red',
            fontWeight: 'bold',
            fontSize: '0.9rem',
            fontStyle: 'italic',
          }
        }}
      />

      <Dialog open={nameDialogOpen} onClose={handleNameDialogClose} maxWidth="xs" fullWidth>
        <DialogTitle>Change / Add Customer Name</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Customer Name"
            type="text"
            fullWidth
            variant="outlined"
            value={nameInputValue}
            onChange={(e) => setNameInputValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleNameDialogConfirm(); }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleNameDialogClose} color="inherit">Cancel</Button>
          <Button
            onClick={handleNameDialogConfirm}
            variant="contained"
            color="primary"
            disabled={!nameInputValue.trim()}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  const renderGstDetails = () => {
    if (!isNormalRegistered || isForeignEntity) return null;

    const { city, district, state, country, pin } = parseAddress(gstDetails?.principalPlaceAddress || '');

    return (
      <>
        <Typography variant="h6" color="secondary" sx={{ mt: 2 }}>
          GST Details
        </Typography>
        <Divider />
        <Typography><strong>Legal Name:</strong> {gstDetails?.legalName || '-'}</Typography>
        <Typography><strong>Trade Name:</strong> {gstDetails?.tradeName || '-'}</Typography>
        <Typography><strong>Registration #:</strong> {gstDetails?.registrationNumber || '-'}</Typography>
        <Typography><strong>Valid From:</strong> {gstDetails?.validityFrom || '-'}</Typography>
        <Typography><strong>Valid To:</strong> {gstDetails?.validityTo || '-'}</Typography>
        <Typography><strong>Address:</strong> {gstDetails?.principalPlaceAddress || '-'}</Typography>
        {gstDetails?.principalPlaceAddress && (
          <>
            <Typography><strong>City:</strong> {gstDetails?.city || city || '-'}</Typography>
            <Typography><strong>District:</strong> {gstDetails?.district || district || '-'}</Typography>
            <Typography><strong>State:</strong> {state || '-'}</Typography>
            <Typography><strong>Country:</strong> {country || '-'}</Typography>
            <Typography><strong>Pin Code:</strong> {pin || '-'}</Typography>
          </>
        )}
        <FormControlLabel
          control={
            <Checkbox
              checked={checked}
              onChange={onCheckboxChange}
              color="primary"
            />
          }
          label="Invoice to a different address than the one on the GST certificate"
          sx={{
            mt: 1,
            '& .MuiFormControlLabel-label': {
              color: 'red',
              fontWeight: 'bold',
              fontSize: '0.9rem',
              fontStyle: 'italic',
            }
          }}
        />
        <AddressDialog
          open={open}
          onClose={onClose}
          additionalAddress={additionalAddress}
          onInputChange={onInputChange}
        />
        {additionalAddress?.address && (
          <>
            <Typography variant="h6" color="secondary" sx={{ mt: 2 }}>
              Additional Business Address
            </Typography>
            <Divider />
            <Typography><strong>Address:</strong> {additionalAddress.address || '-'}</Typography>
            <Typography><strong>City:</strong> {additionalAddress.city || '-'}</Typography>
            <Typography><strong>District:</strong> {additionalAddress.district || '-'}</Typography>
            <Typography><strong>State:</strong> {additionalAddress.state || '-'}</Typography>
            <Typography><strong>Pin Code:</strong> {additionalAddress.pincode || '-'}</Typography>
          </>
        )}
      </>
    );
  };

  const renderAddressProof = () => {
    if (isNormalRegistered && !isForeignEntity) return null;

    const changeAddr = panDetails?.ChangeAddress;

    return (
      <>
        <Typography variant="h6" color="secondary" sx={{ mt: 2 }}>
          Address Proof
        </Typography>
        <Divider />
        <Typography>
          <strong>Type of Document:</strong> {altDocType ? (altDocType === 'passport' ? 'Passport' : altDocType === 'dl' ? "Driver's License" : 'Voter ID') : '-'}
        </Typography>
        {!altDocType && (
          <Typography>
            <strong>Address:</strong> {changeAddr?.address ?? aadhaarDetails?.address ?? '-'}
          </Typography>
        )}
        {changeAddr && (
          <>
            {changeAddr.city && <Typography><strong>City:</strong> {changeAddr.city}</Typography>}
            {changeAddr.district && <Typography><strong>District:</strong> {changeAddr.district}</Typography>}
            {changeAddr.state && <Typography><strong>State:</strong> {changeAddr.state}</Typography>}
            {changeAddr.pincode && <Typography><strong>Pin Code:</strong> {changeAddr.pincode}</Typography>}
          </>
        )}

        <FormControlLabel
          control={
            <Checkbox
              checked={addressChecked}
              onChange={handleAddressCheckboxChange}
              onClick={handleAddressCheckedClick}
              color="primary"
            />
          }
          label="Change Address / Add Address"
          sx={{
            mt: 0.5,
            '& .MuiFormControlLabel-label': {
              color: 'red',
              fontWeight: 'bold',
              fontSize: '0.9rem',
              fontStyle: 'italic',
            }
          }}
        />

        <Dialog open={addressDialogOpen} onClose={handleAddressDialogClose} maxWidth="xs" fullWidth>
          <DialogTitle>Change / Add Address</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Address"
              type="text"
              fullWidth
              variant="outlined"
              value={addressInputValue.address}
              onChange={(e) => setAddressInputValue(prev => ({ ...prev, address: e.target.value }))}
            />
            <TextField
              margin="dense"
              label="City"
              type="text"
              fullWidth
              variant="outlined"
              value={addressInputValue.city}
              onChange={(e) => setAddressInputValue(prev => ({ ...prev, city: e.target.value }))}
            />
            <TextField
              margin="dense"
              label="District"
              type="text"
              fullWidth
              variant="outlined"
              value={addressInputValue.district}
              onChange={(e) => setAddressInputValue(prev => ({ ...prev, district: e.target.value }))}
            />
            <TextField
              margin="dense"
              label="State"
              type="text"
              fullWidth
              variant="outlined"
              value={addressInputValue.state}
              onChange={(e) => setAddressInputValue(prev => ({ ...prev, state: e.target.value }))}
            />
            <TextField
              margin="dense"
              label="Pin Code"
              type="text"
              fullWidth
              variant="outlined"
              value={addressInputValue.pincode}
              onChange={(e) => setAddressInputValue(prev => ({ ...prev, pincode: e.target.value }))}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleAddressDialogClose} color="inherit">Cancel</Button>
            <Button
              onClick={handleAddressDialogConfirm}
              variant="contained"
              color="primary"
              disabled={!addressInputValue.address.trim()}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  };

  const renderTanDetails = () => {
    if (!tanDetails?.tanNumber) return null;

    return (
      <>
        <Typography variant="h6" color="secondary" sx={{ mt: 2 }}>
          TAN Details
        </Typography>
        <Divider />
        <Typography><strong>TAN Number:</strong> {tanDetails.tanNumber}</Typography>
      </>
    );
  };

  const renderIncorporationCertificate = () => {
    const showForCustomers = [
      'Private Limited/ Public Limited',
      'LLP',
      'Registered Partnership',
      'Foreign Entity'
    ];

    if (!showForCustomers.includes(statusOfCustomer) || !incFileLink) return null;

    return (
      <>
        <Typography variant="h6" color="secondary" sx={{ mt: 2 }}>
          Certificate of Incorporation
        </Typography>
        <Divider />
        <Typography>
          <a href={incFileLink} target="_blank" rel="noopener noreferrer">
            View document
          </a>
        </Typography>
      </>
    );
  };

  const renderPartnershipDeed = () => {
    if (statusOfCustomer !== 'Registered Partnership' || !partnershipFileLink) return null;

    return (
      <>
        <Typography variant="h6" color="secondary" sx={{ mt: 2 }}>
          Partnership Deed
        </Typography>
        <Divider />
        <Typography>
          <a href={partnershipFileLink} target="_blank" rel="noopener noreferrer">
            View document
          </a>
        </Typography>
      </>
    );
  };

  const renderPassbookFrontPage = () => {
    const showForCustomers = [
      'Private Limited/ Public Limited',
      'LLP',
      'Unregistered Partnership',
      'Society/AOP/HUF/Trust',
    ];

    if (!showForCustomers.includes(statusOfCustomer) || gstStatus !== 'Un-Registered' || !passbookFileLink) return null;

    return (
      <>
        <Typography variant="h6" color="secondary" sx={{ mt: 2 }}>
          Passbook Front Page
        </Typography>
        <Divider />
        <Typography>
          <a href={passbookFileLink} target="_blank" rel="noopener noreferrer">
            View document
          </a>
        </Typography>
      </>
    );
  };

  const renderChequeDetails = () => {
    if (isForeignEntity) return null;

    return (
      <>
        <Typography variant="h6" color="secondary" sx={{ mt: 2 }}>
          Cancelled Cheque
        </Typography>
        <Divider />
        <Typography><strong>Bank Name:</strong> {chequeDetails.bankName || bankDetails.bankName || '-'}</Typography>
        <Typography><strong>IFSC:</strong> {chequeDetails.ifsc || bankDetails.ifsc || '-'}</Typography>
        <Typography><strong>Account #:</strong> {chequeDetails.accountNumber || bankDetails.accountNumber || '-'}</Typography>
        <Typography><strong>Cheque #:</strong> {chequeDetails.chequeNumber || bankDetails.chequeNumber || '-'}</Typography>
      </>
    );
  };

  const renderTypeOfDocument = () => {
    if (gstStatus === 'Normal Registered' || statusOfCustomer !== 'Proprietor / Individual') return null;

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6" color="secondary">Type of Document</Typography>
        <Divider />
        <Typography>
          <strong>Type of Document:</strong> {altDocType ? (altDocType === 'passport' ? 'Passport' : altDocType === 'dl' ? "Driver's License" : 'Voter ID') : '-'}
        </Typography>
        {!altDocType && (
          <Typography>
            <strong>Address:</strong> {aadhaarDetails?.address || '-'}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {renderPanDetails()}
      {renderGstDetails()}
      {renderAddressProof()}
      {renderTanDetails()}
      {renderIncorporationCertificate()}
      {renderPartnershipDeed()}
      {renderPassbookFrontPage()}
      {renderChequeDetails()}
      {renderTypeOfDocument()}
    </Box>
  );
};

export default ReviewSection;